import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import type { BudgetSetting } from "../../api/budgetSettingsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** One line saying where the in-effect value came from.
 *
 *  Uses the server-computed `source` rather than comparing values: a company
 *  that deliberately chose the same number as the platform default IS
 *  overridden, and must not silently start following the default when it
 *  changes. */
function sourceLabel(source: "company" | "platform" | "default"): string {
  if (source === "company") return "Set for this organization.";
  if (source === "platform") return "Inherited from the platform default.";
  return "Not set anywhere.";
}

/** The monthly spend guard for this organization.
 *
 *  TWO CONTROLS, TWO DIFFERENT GATES — enforced by the API, mirrored here:
 *
 *  - the on/off switch is the company's own call (company admin);
 *  - the LIMIT is platform-admin only, because a cap the capped party can
 *    raise is not a cap. `limit_editable` says whether THIS caller may change
 *    it, so the field explains itself rather than 403-ing on save.
 *
 *  A null limit means NO CAP, which is deliberately distinct from 0 — zero
 *  blocks every turn and is a legitimate way to pause spend.
 */
export function BudgetSettings() {
  const api = useAuthedApi();
  const [setting, setSetting] = useState<BudgetSetting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [limitDraft, setLimitDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const s = await api.budgetSettings.load();
      setSetting(s);
      setLimitDraft(s.limit_usd === null ? "" : String(s.limit_usd));
    } catch (e) {
      setError(message(e));
    }
  }, [api]);

  // `loadedRef` rather than a plain dependency list: useAuthedApi() returns a
  // fresh object every render, so an unguarded effect refires on each one and
  // wipes the error or notice the user is reading.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const toggle = async (enabled: boolean) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      // The RESPONSE becomes the state, not the submitted value: the server
      // recomputes `source`, which flips to "platform"/"default" the moment a
      // field is cleared.
      setSetting(await api.budgetSettings.saveGuard({ enabled }));
      setNotice(enabled ? "Budget guard on." : "Budget guard off.");
    } catch (e) {
      setError(message(e));
    } finally {
      setSaving(false);
    }
  };

  const saveLimit = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const trimmed = limitDraft.trim();
      // Empty CLEARS, so the organization inherits again. Sending 0 instead
      // would pin a cap that blocks every turn — the opposite of "unset".
      const patch = trimmed === "" ? { limit_usd: null } : { limit_usd: Number(trimmed) };
      if (trimmed !== "" && !Number.isFinite(patch.limit_usd as number)) {
        setError("Enter a number, or leave it empty to inherit.");
        return;
      }
      const saved = await api.budgetSettings.saveLimit(patch);
      setSetting(saved);
      setLimitDraft(saved.limit_usd === null ? "" : String(saved.limit_usd));
      setNotice(trimmed === "" ? "Limit cleared — inheriting." : "Limit saved.");
    } catch (e) {
      setError(message(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Spend guard"
        subtitle="Cap this organization's monthly LLM spend. Off unless you turn it on."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <WorkspaceCard>
        <Stack spacing={3}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!setting?.enabled}
                  disabled={!setting || saving}
                  onChange={(e) => void toggle(e.target.checked)}
                />
              }
              label="Meter this organization's spend"
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              {setting ? sourceLabel(setting.enabled_source) : "Loading…"} When on,
              turns switch to a cheaper model as you approach the limit, and stop
              once it is reached.
            </Typography>
          </Box>

          <Box>
            <TextField
              label="Monthly limit (USD)"
              value={limitDraft}
              onChange={(e) => setLimitDraft(e.target.value)}
              disabled={!setting?.limit_editable || saving}
              onBlur={() => {
                if (setting?.limit_editable) void saveLimit();
              }}
              placeholder="No cap"
              size="small"
              sx={{ maxWidth: 240 }}
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              {setting && !setting.limit_editable
                ? "Only a platform admin can change the limit."
                : "Leave empty for no cap. 0 stops all spend."}{" "}
              {setting ? sourceLabel(setting.limit_source) : ""}
            </Typography>
          </Box>
        </Stack>
      </WorkspaceCard>
    </Box>
  );
}
